import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
};

const getPageItems = (currentPage, totalPages, siblingCount = 1) => {
  const totalPageNumbers = siblingCount * 2 + 5;

  if (totalPages <= totalPageNumbers) {
    return range(1, totalPages);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    return [...range(1, leftItemCount), 'ellipsis-right', totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    return [1, 'ellipsis-left', ...range(totalPages - rightItemCount + 1, totalPages)];
  }

  return [1, 'ellipsis-left', ...range(leftSibling, rightSibling), 'ellipsis-right', totalPages];
};

const pageButtonClass = (isActive) =>
  `relative inline-flex items-center min-w-[2.25rem] justify-center px-3 py-2 text-sm font-semibold ${
    isActive
      ? 'z-10 bg-[#009efb] text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#009efb]'
      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
  }`;

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (!totalPages || totalPages <= 1) {
    return null;
  }

  const safeCurrent = Math.min(Math.max(currentPage || 1, 1), totalPages);
  const pageItems = getPageItems(safeCurrent, totalPages);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === safeCurrent) {
      return;
    }
    onPageChange(page);
  };

  const renderPageItems = () =>
    pageItems.map((item) => {
      if (typeof item === 'string') {
        return (
          <span
            key={item}
            className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 ring-1 ring-inset ring-gray-300"
          >
            ...
          </span>
        );
      }

      return (
        <button
          key={item}
          type="button"
          onClick={() => goToPage(item)}
          aria-current={item === safeCurrent ? 'page' : undefined}
          className={pageButtonClass(item === safeCurrent)}
        >
          {item}
        </button>
      );
    });

  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex flex-1 items-center justify-between sm:hidden gap-2">
        <button
          type="button"
          onClick={() => goToPage(safeCurrent - 1)}
          disabled={safeCurrent === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          {safeCurrent} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => goToPage(safeCurrent + 1)}
          disabled={safeCurrent === totalPages}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700">
          Showing page <span className="font-medium">{safeCurrent}</span> of{' '}
          <span className="font-medium">{totalPages}</span>
        </p>
        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
          <button
            type="button"
            onClick={() => goToPage(safeCurrent - 1)}
            disabled={safeCurrent === 1}
            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          {renderPageItems()}
          <button
            type="button"
            onClick={() => goToPage(safeCurrent + 1)}
            disabled={safeCurrent === totalPages}
            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
